/**
 * Ads for **Melodimals** — domain layer (the ONLY app-specific part).
 *
 * The rest of the kit (`types`/`gate`/`caps`/`config`/`provider`/`placements`/
 * `rewards`) comes from the portfolio canon (listto, `876e3b27`) and is kept
 * identical on purpose: what changes per app is this file.
 *
 * Melodimals' row in the per-app plan (§10 of the strategy): tier **K**,
 * formats **B(G) + soft I**, breakpoints *between-games* (never within a
 * game) and the progress screen.
 *
 * ── Why there is NO `rewarded` placement ─────────────────────────────────────
 * The child regime (§6.3) allows rewarded only after the adult gate and
 * **without rewards that generate compulsion**, and catalog rule §5 says
 * a reward must **reuse value that ALREADY exists** in the app. In
 * Melodimals that value does not exist: the three instruments, ten songs, and
 * four mini-games are free and there is nothing locked to unlock.
 * Declaring a rewarded ad here would force us to invent an economy (coins,
 * artificially blocked content) — exactly what §6.3 forbids in a children's
 * app, where the intended path is for the adult to buy premium.
 *
 * So the registry is **empty of rewarded**, and that is a decision with a test
 * (`domain.test.ts`), not an oversight. The child rewarded-ad gate machinery
 * (deny without the adult gate) remains tested in `gate.test.ts`: the day
 * real premium content exists, plugging it in means declaring the placement.
 */

import { definePlacements } from "./placements";
import type { AdTier } from "./types";

/** Melodimals ad tier (§9): **K — child**, COPPA/Families regime. */
export const MELODIMALS_AD_TIER: AdTier = "K";

/**
 * Melodimals placement registry. It is the source of truth: a breakpoint
 * that is not here cannot render an ad, and the test guard checks that all
 * are legal for tier K.
 */
export const MELODIMALS_AD_PLACEMENTS = definePlacements({
  /**
   * Transition between the toy scene and a module — the ONLY legal interstitial
   * in the app. The `between-screens` breakpoint is what makes the gate
   * allow it: any other one (including `done-screen`) is denied under the
   * child regime, because a mini-game's "done" screen is still
   * inside the game.
   */
  "between-games": { kind: "interstitial", breakpoint: "between-screens" },
  /**
   * Discreet banner anchored at `/progress` (the statistics screen): it is
   * passive, viewed by an adult with the child, and **outside the play area**.
   * Never on the home (it would break the toy scene) or on a game
   * screen, and never where it could push content and force
   * scrolling — the product's strictest UX restriction.
   */
  "progress-banner": { kind: "banner", breakpoint: "stats" },
} as const);

export type MelodimalsPlacementId = keyof typeof MELODIMALS_AD_PLACEMENTS;

/**
 * Surfaces where an ad is prohibited no matter what. It is the list of
 * game routes: if the user is on any of them, the app declares
 * `inFlight` and the gate denies it before looking at any counter.
 *
 * It is checked by PREFIX so a new route under `/games/…` is covered
 * the day it is added, instead of inheriting permission by oversight.
 */
export const IN_GAME_ROUTE_PREFIXES = ["/games/", "/instrument/", "/songs/"] as const;

/** Is this route a game in progress? (⇒ never an ad, §6.2). */
export function isInGameRoute(path: string): boolean {
  return IN_GAME_ROUTE_PREFIXES.some((prefix) => path.startsWith(prefix));
}
